import React, { useState, useEffect } from 'react';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import Dashboard from './components/Dashboard/Dashboard';
import ProductTable from './components/ProductTable/ProductTable';
import Alerts from './components/Alerts/Alerts';
import ConsumoPersonal from './components/ConsumoPersonal/ConsumoPersonal';
import ClipTransactions from './components/ClipTransactions/ClipTransactions';
import Recipes from './components/Recipes/Recipes';
import Movimientos from './components/Movimientos/Movimientos';
import Plataformas from './components/Plataformas/Plataformas';
import DatabaseMigration from './components/DatabaseMigration/DatabaseMigration';
import Login from './components/Login/Login';
import { Producto } from './types';
import { useInventory } from './hooks/useInventory';
import { authService } from './services/auth';
import { useDeviceDetect } from './hooks/useDeviceDetect';

function App() {
  const [activeView, setActiveView] = useState(authService.getUser()?.role === 'user' ? 'inventario' : 'dashboard');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [checkingAuth, setCheckingAuth] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { isTablet, isMobile } = useDeviceDetect();
  const { 
    productos, 
    loading, 
    error, 
    stats, 
    updateProduct,
    consumosPersonal,
    configuracionConsumo,
    registrarConsumoPersonal,
    useMockData
  } = useInventory();

  useEffect(() => {
    // Check if user is authenticated on mount
    const checkAuth = async () => {
      setCheckingAuth(true);
      try {
        const isValid = await authService.verifyToken();
        setIsAuthenticated(isValid);
      } catch (error) {
        console.error('Error checking auth:', error);
        setIsAuthenticated(false);
      } finally {
        setCheckingAuth(false);
      }
    };
    
    checkAuth();
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    const user = authService.getUser();
    if (user?.role === 'user') {
      setActiveView('inventario');
    }
  };

  const handleLogout = () => {
    authService.logout();
    setIsAuthenticated(false);
  };

  const handleEditProduct = async (product: Producto) => {
    console.log('Editando producto:', product);
    await updateProduct(product);
  };


  const renderContent = () => {
    const inventoryDependentLoading = !['ventas', 'plataformas', 'movimientos', 'migration'].includes(activeView);
    if (loading && inventoryDependentLoading) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
            <p className="mt-4 text-medium-gray">Cargando inventario...</p>
          </div>
        </div>
      );
    }

    // Views that don't depend on the inventory hook can render even if there's an error
    const inventoryIndependentViews = ['ventas', 'plataformas', 'movimientos', 'migration'];

    if (error && !inventoryIndependentViews.includes(activeView)) {
      return (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center max-w-md">
            <div className="text-red-500 mb-4">
              <svg className="w-16 h-16 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-dark-text mb-2">Error de Conexión</h3>
            <p className="text-red-600 mb-4">{error}</p>
            <p className="text-sm text-medium-gray">
              Por favor, verifique que el servidor MongoDB esté funcionando correctamente.
            </p>
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent/90 transition-colors"
            >
              Reintentar
            </button>
          </div>
        </div>
      );
    }

    switch (activeView) {
      case 'dashboard':
        return <Dashboard stats={stats} useMockData={useMockData} />;
      case 'inventario':
        return <ProductTable productos={productos} onEditProduct={handleEditProduct} />;
      case 'ventas':
        return <ClipTransactions />;
      case 'recetas':
        return <Recipes />;
      case 'movimientos':
        return <Movimientos />;
      case 'plataformas':
        return <Plataformas />;
      case 'consumo':
        return (
          <ConsumoPersonal
            consumos={consumosPersonal}
            configuracion={configuracionConsumo}
            onRegistrarConsumo={registrarConsumoPersonal}
          />
        );
      case 'alertas':
        return <Alerts />;
      case 'migration':
        return <DatabaseMigration />;
      default:
        return <Dashboard stats={stats} />;
    }
  };

  // Show loading while checking auth
  if (checkingAuth) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent mx-auto"></div>
          <p className="mt-4 text-medium-gray">Verificando autenticación...</p>
        </div>
      </div>
    );
  }

  // Show login if not authenticated
  if (!isAuthenticated) {
    return <Login onLoginSuccess={handleLoginSuccess} />;
  }

  const user = authService.getUser();

  return (
    <div className="min-h-screen bg-cream">
      <Header 
        onLogout={handleLogout} 
        user={user}
        onMenuToggle={() => setSidebarOpen(!sidebarOpen)}
        onRefresh={() => window.location.reload()}
      />
      <div className="flex">
        <Sidebar 
          activeView={activeView} 
          onViewChange={setActiveView} 
          userRole={user?.role || 'user'}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
        <main className={`flex-1 ${!(isTablet || isMobile) ? 'pl-4 pr-6' : 'p-4'} py-4 max-w-full`}>
          {renderContent()}
        </main>
      </div>
    </div>
  );
}

export default App;
